"use client";

/**
 * AgentSupervisionWidget — status counts + optional pause / open actions.
 * Plan-gated copy stays honest (no fake agent metrics).
 */
import React, { useState } from "react";
import { Modal, message } from "antd";
import {
  ClusterOutlined,
  ArrowRightOutlined,
  PauseCircleOutlined,
} from "@ant-design/icons";
import type { AgentSupervisionCounts } from "./lib/types";
import { WidgetChrome } from "./WidgetChrome";
import styles from "./WidgetChrome.module.css";
import local from "./AgentSupervisionWidget.module.css";

export interface AgentSupervisionWidgetProps {
  counts: AgentSupervisionCounts;
  loading?: boolean;
  error?: string | null;
  /** Entitled to run/supervise agents */
  canUseAgents: boolean;
  planLoading?: boolean;
  isFree?: boolean;
  className?: string;
  onOpenAgents: () => void;
  onUpgrade?: () => void;
  /**
   * Pause all running agents (caller supplies mutation).
   * High-friction confirm is handled inside the widget.
   */
  onPauseRunning?: () => Promise<{ ok: boolean; error?: string }>;
  pauseLoading?: boolean;
  /** When false, hide Open agents (already on /ai-trading). Upgrade CTA still shows if locked. */
  showOpenCta?: boolean;
}

export function AgentSupervisionWidget({
  counts,
  loading = false,
  error = null,
  canUseAgents,
  planLoading = false,
  isFree = false,
  className,
  onOpenAgents,
  onUpgrade,
  onPauseRunning,
  pauseLoading = false,
  showOpenCta = true,
}: AgentSupervisionWidgetProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const description = planLoading
    ? "Checking plan access…"
    : isFree || !canUseAgents
      ? "Free desk includes a paper agent. Operator unlocks live chat; Desk brings a live agent."
      : error
        ? "Agent status unavailable — refresh this list."
        : loading
          ? "Loading agent status…"
          : counts.total === 0
            ? "No agents configured yet. Deploy or create agents on the agent desk."
            : "Status counts for agents on this desk.";

  const showPause =
    canUseAgents &&
    !planLoading &&
    counts.running > 0 &&
    typeof onPauseRunning === "function";

  const handlePauseConfirm = async () => {
    if (!onPauseRunning) return;
    const result = await onPauseRunning();
    setConfirmOpen(false);
    if (result.ok) {
      message.success(
        counts.running === 1
          ? "Agent pause requested"
          : `Pause requested for ${counts.running} agents`
      );
    } else {
      message.error(result.error || "Could not pause agents");
    }
  };

  return (
    <>
      <WidgetChrome
        label="Agents"
        meta="Supervision"
        aria-label="Agent supervision"
        className={className}
        bezelExtra={
          showPause ? (
            <button
              type="button"
              className={local.pauseBtn}
              onClick={() => setConfirmOpen(true)}
              disabled={pauseLoading}
              aria-label={`Pause ${counts.running} running agent${counts.running === 1 ? "" : "s"
                }`}
            >
              <PauseCircleOutlined aria-hidden /> Pause running
            </button>
          ) : null
        }
      >
        <div className={local.layout}>
          <div className={local.lead}>
            <ClusterOutlined className={local.icon} aria-hidden />
            <div className={local.copy}>
              <p className={local.title}>Trading agents</p>
              <p className={local.desc}>{description}</p>
            </div>
          </div>

          {(canUseAgents || isFree) && !planLoading ? (
            <div
              className={local.stats}
              role="group"
              aria-label="Agent status counts"
            >
              {(
                [
                  ["Running", counts.running, false],
                  ["Paused", counts.paused, false],
                  ["Error", counts.error, counts.error > 0],
                  ["Waiting", counts.waiting, false],
                ] as const
              ).map(([lbl, n, isErr]) => (
                <div key={lbl} className={local.stat}>
                  <span
                    className={`${styles.mono} ${styles.tabular} ${local.statValue} ${!loading && isErr ? local.statError : ""
                      }`}
                  >
                    {loading ? "—" : n}
                  </span>
                  <span className={local.statLabel}>{lbl}</span>
                </div>
              ))}
            </div>
          ) : null}

          {showOpenCta || !canUseAgents ? (
            <div className={local.actions}>
              <button
                type="button"
                className={canUseAgents ? styles.ctaGhost : styles.cta}
                onClick={() =>
                  canUseAgents ? onOpenAgents() : onUpgrade?.() ?? onOpenAgents()
                }
              >
                {canUseAgents ? (
                  <>
                    Open agents <ArrowRightOutlined aria-hidden />
                  </>
                ) : (
                  <>
                    Upgrade for agents <ArrowRightOutlined aria-hidden />
                  </>
                )}
              </button>
            </div>
          ) : null}
        </div>
      </WidgetChrome>

      <Modal
        title="Pause running agents?"
        open={confirmOpen}
        onOk={() => void handlePauseConfirm()}
        onCancel={() => setConfirmOpen(false)}
        okText="Pause agents"
        cancelText="Cancel"
        confirmLoading={pauseLoading}
        okButtonProps={{ danger: false }}
        destroyOnHidden
      >
        <p>
          This pauses <strong>{counts.running}</strong> running agent
          {counts.running === 1 ? "" : "s"}. Open orders from agents may remain
          until canceled on the trading desk.
        </p>
      </Modal>
    </>
  );
}
